import React, { useContext } from 'react';
import cn from 'classnames';
import Zone, { Props as ZoneProps } from '../Zone';
import { hashString } from './utils';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import AddWidget from '../AddWidget';
import useGruser from '../../hooks/useGruser';
import { useSelector } from 'react-redux';
import { getLayout } from '../../redux/selectors/layout';
import { getAllModules } from '../../redux/selectors/modules';
import {
  filterAlreadyInstalledAndCoreRestrictedModules,
  getInstallableWidgets,
} from '../Zone/utils';
import { ModuleName } from '../../types/modules';
import { WidgetConfig } from '../../types/widget';
import { useLayoutActions } from '../../hooks/useLayoutActions';

import s from './ZoneGroup.scss';

export interface Props {
  className?: string;
  children?: React.ReactNode;
  disableTopAddWidget?: boolean;
  withStaticAddWidget?: boolean; // this makes add widget gizmo always visible (not only on hover)
  addWidgetLabel?: string;
  widgetConfigs: Partial<Record<ModuleName, WidgetConfig>>;
}

const ZoneGroup: React.FC<Props> = ({
  children,
  className,
  disableTopAddWidget = false,
  withStaticAddWidget,
  addWidgetLabel,
  widgetConfigs,
}) => {
  const isMobile = useContext(MobileContext);
  const { canAddModule } = useLayoutActions();

  const { gruser } = useGruser();
  const allModules = useSelector<any, any>(state =>
    getAllModules(state, gruser)
  );
  const layout = useSelector(state => getLayout(state, gruser));
  const { zones } = layout || {};
  if (!zones) {
    return null;
  }

  /**
   * Takes an array of child components and injects AddWidget components in
   * between every two Zones, and also one at the top and one at the bottom of
   * the ZoneGroup.
   *
   * +-----------------+-----------------+
   * | It turns this:  | Into this:      |
   * +-----------------+-----------------+
   * |                 |                 |
   * | <ZoneGroup>     | <ZoneGroup>     |
   * |                 |   <AddWidget>   |
   * |   <Zone>        |   <Zone>        |
   * |                 |   <AddWidget>   |
   * |   <Zone>        |   <Zone>        |
   * |                 |   <AddWidget>   |
   * |   <Zone>        |   <Zone>        |
   * |   ...           |   ...           |
   * |                 |   <AddWidget>   |
   * |   <Zone>        |   <Zone>        |
   * |                 |   <AddWidget>   |
   * | </ZoneGroup>    | </ZoneGroup>    |
   * |                 |                 |
   * +-----------------+-----------------+
   *
   * Each Zone used to render one AddWidget component above itself, and one
   * below itself. This resulted in having two AddWidgets between every two
   * Zone components. The Zone is now responsible for rendering the AddWidget
   * gizmos between the widgets, and it is now the responsibility of the
   * ZoneGroup to inject the AddWidget gizmos between the Zones. These
   * AddWidget gizmos now allow the user to install the widgets from both the
   * Zone above and the one bellow the AddWidget gizmo.
   *
   * The complexity of this implementation comes from the following requirements:
   *  - ZoneGroup can contain non-Zone components which should break the flow,
   *    and any widgets above should render the bottom AddWidget gizmo, and any
   *    widgets below should render the top AddWidget gizmos
   *  - Zone that is locked behaves like the non-Zone component above
   *  - Zone that is locked but empty, should collapse
   *  - ZoneGroup should skip rendering the top AddWidget if disableTopAddWidget is true
   *  - ZoneGroup must render the top AddWidget if all Zones are empty
   *  - If there is a non-locked Zone above it, installableModules for the
   *    current Zone should show in previous Zone's bottom AddWidget, together
   *    with previous zone's installableModules, bottom AddWidget gizmo should show
   *  - If there are no non-locked Zone's above it, Zone should render both top
   *    and bottom AddWidget
   *  - Empty Zone's should collapse and show its installableModules in
   *    previous or next AddWidget gizmo
   *  - etc.
   *
   * To make things more streamlined and hopefully easier to follow, this is
   * implemented as a series of small array reducers that transform component's
   * children array.  Each reducer does one small thing, which hopefully, will
   * make this process less tedious to understand.
   * First, we take all the children, and transform only Zone components into
   * simple JSON structures that expose some of the Zone metadata that is going
   * to be used by each of the subsequent array reducers (this JSON structure
   * is referred to as the "ZoneData"). We then transform the children array
   * using the metadata from the ZoneData to decide where should we put the
   * AddWidget gizmos, and then in the last step, we finally transform that
   * ZoneData into actual AddWidget and Zone components.
   *
   **/
  const renderChildrenWithAddWidgets = () => {
    return (
      React.Children.toArray(children)

        // Step 1: Convert Zone components to ZoneData
        .map((child: React.ReactElement<{}>) => {
          // Leave non-Zone components intact
          if (!React.isValidElement(child) || child.type !== Zone) {
            return child;
          }

          const { name: zone, locked } = child.props as ZoneProps;
          const { installedModulesIds = [], installableModuleTypes = [] } =
            zones[zone] || {};
          const isZoneEmpty = installedModulesIds.length === 0;
          const installableModuleTypesFiltered =
            filterAlreadyInstalledAndCoreRestrictedModules(
              installableModuleTypes,
              installedModulesIds.map(id => allModules[id])
            );

          // Convert the Zone component into a "ZoneData" object that keeps a
          // reference to the actual Zone component and adds some metadata
          // used by the other steps.
          return {
            component: child,
            isZoneData: true,
            isZoneEmpty,
            isZoneLocked: locked,
            installableModulesTop: installableModuleTypesFiltered.map(
              module => ({
                ...module,
                zone,
                position: 0,
              })
            ),
            installableModulesBottom: installableModuleTypesFiltered.map(
              module => ({
                ...module,
                zone,
                position: installedModulesIds.length,
              })
            ),
          };
        })

        // Step 2: Remove empty locked Zones
        .reduce((result, child: any) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData || !child.isZoneLocked) {
            return [...result, child];
          }
          // if Zone is locked and empty, just omit it from the resultset
          if (child.isZoneEmpty) {
            return result;
          }
          // move it to the next step
          return [...result, child];
        }, [])

        // Step 3: Try to collapse empty Zones into the previous ZoneData
        .reduce((result, child: any) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }
          const prevIndex = result.length - 1;
          const prev = result[prevIndex];
          // if previous child is not ZoneData, or is locked, we cannot merge, so do nothing
          if (!prev || !prev.isZoneData || prev.isZoneLocked) {
            return [...result, child];
          }
          // If the current zone is empty (has no installed modules)
          // merge its installableModulesTop into previous child's installableModulesBottom
          if (child.isZoneEmpty) {
            prev.installableModulesBottom = [
              ...prev.installableModulesBottom,
              ...child.installableModulesTop,
            ];
            child.installableModulesTop = false;
            // omit this empty zone from the resultset
            return result;
          }
          return [...result, child];
        }, [])

        // Step 4: Try to collapse empty Zones into the next ZoneData
        .reduce((result, child) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }
          const prevIndex = result.length - 1;
          const prev = result[prevIndex];
          // if previous child is not ZoneData, or is not empty, there is nothing to do
          if (!prev || !prev.isZoneData || !prev.isZoneEmpty) {
            return [...result, child];
          }

          // Previous item is empty, move current child to its place, and merge
          // the installableModules
          result[prevIndex] = {
            ...child,
            installableModulesTop: [
              ...prev.installableModulesBottom,
              ...child.installableModulesTop,
            ],
          };
          return result;
        }, [])

        // Step 5: If there are empty Zones that couldn't be collapsed, make
        // them only show the bottom AddWidget
        .reduce((result, child) => {
          if (child.isZoneData && child.isZoneEmpty) {
            child.installableModulesTop = false;
          }
          return [...result, child];
        }, [])

        // Step 6: Merge top and bottom installableModules between two adjoining Zones
        .reduce((result, child) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }
          const prevIndex = result.length - 1;
          const prev = result[prevIndex];
          // if previous child is not ZoneData, or is locked, we cannot merge, so do nothing
          if (!prev || !prev.isZoneData || prev.isZoneLocked) {
            return [...result, child];
          }

          prev.installableModulesBottom = [
            ...prev.installableModulesBottom,
            ...child.installableModulesTop,
          ];
          child.installableModulesTop = false;
          return [...result, child];
        }, [])

        // Step 7: Remove the top AddWidget if disableTopAddWidget is true
        .reduce((result, child) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }

          if (disableTopAddWidget) {
            child.installableModulesTop = false;
            disableTopAddWidget = false;
          }
          return [...result, child];
        }, [])

        // Step 8: Expand ZoneData back into the actual AddWidget and Zone components
        .reduce((result, child) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }
          return [
            ...result,
            renderAddWidget(child.installableModulesTop, 'group-top'),
            child.component,
            renderAddWidget(child.installableModulesBottom, 'group-bottom'),
          ];
        }, [])
        .filter(x => x)
    );
  };

  const renderAddWidget = (installableModules, position) => {
    if (isMobile || !installableModules || !installableModules.length) {
      return null;
    }

    // Make sure there are no duplicates
    installableModules = installableModules.reduce((result, child) => {
      if (result.findIndex(item => item.moduleName === child.moduleName) >= 0) {
        return [...result];
      }
      return [...result, child];
    }, []);

    const uniqueKey = hashString(
      installableModules
        .reduce(
          (result, { zone, moduleName, position: positionR }) => [
            ...result,
            `${zone}.${moduleName}.${position}.${positionR}`,
          ],
          []
        )
        .join(':')
    );

    return (
      <AddWidget
        key={`add-widget-${uniqueKey}`}
        customLabel={addWidgetLabel}
        installableWidgets={getInstallableWidgets(
          widgetConfigs,
          installableModules
        )}
      />
    );
  };

  return (
    <div
      className={cn(
        s['root'],
        withStaticAddWidget && s['static-add-widget'],
        className
      )}
    >
      {canAddModule ? renderChildrenWithAddWidgets() : children}
    </div>
  );
};
ZoneGroup.displayName = 'ZoneGroup';

export default ZoneGroup;
